import type { FaqItem } from "@/components/feature/FaqAccordion";
import { farmReopeningLabel, isFarmOpen, type Farm } from "@/mocks/farms";

/**
 * Builds a tailored set of visitor FAQs for a single farm using its real data,
 * so every farm detail page gets relevant questions and answers.
 */
export const buildFarmFaqs = (farm: Farm): FaqItem[] => {
  const establishedLine = farm.established.toLowerCase().startsWith("since")
    ? ` It has been farmed by the family ${farm.established.toLowerCase()}.`
    : "";

  // A farm that is temporarily closed stays on the site, but the answer must not
  // promise a visit it cannot deliver yet.
  const reopeningLabel = farmReopeningLabel(farm);
  const visitAnswer = isFarmOpen(farm)
    ? `Yes. ${farm.name} is a real, working family farm in ${farm.village}, ${farm.district}, hosted by ${farm.farmer}. Visits are arranged in advance through our ${farm.experienceLabel} experience, so the family can prepare the farm and welcome you properly.`
    : `Not right now — ${farm.name} is temporarily closed and ${
        reopeningLabel ? `expected back by ${reopeningLabel}` : "reopening soon"
      }, so visits cannot be booked yet. The farm is still part of Rythugaru and you can send us an enquiry to be told the moment dates open again.`;

  return [
    {
      q: `Can I visit ${farm.name}?`,
      a: visitAnswer,
    },
    {
      q: `What will we do at ${farm.name}?`,
      a: `You will spend the day doing what the farm actually does during your visit. Highlights here include ${farm.highlights.join(", ")} — everything is hands-on and paced to your group.`,
    },
    {
      q: `Where exactly is the farm located?`,
      a: `${farm.name} sits in ${farm.village} in ${farm.district}, Andhra Pradesh, in the Godavari delta of Konaseema. We share an exact map pin, a route and travel tips once your date is confirmed.`,
    },
    {
      q: `What is grown here and how big is the farm?`,
      a: `The farm covers ${farm.area}, with ${farm.crop} as the main crop.${establishedLine}`,
    },
    {
      q: `Is the farm visit suitable for children and families?`,
      a: `Absolutely. Children are welcome with a guardian and many families join our farm visits. Activities are adjusted so everyone can take part safely, and we can slow the pace if you are visiting with young children.`,
    },
    {
      q: `When is the best time to visit?`,
      a: `Every season shows a different side of the farm. For comfortable weather and the widest choice of activities, October to February is ideal. We host visits year-round and will tell you exactly what is happening on the farm during your dates.`,
    },
  ];
};